import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { AuthLayout } from "./authLayout";
import { AuthHeader } from "./authHeader";
import { Visibility, VisibilityOff, ArrowBack } from "@mui/icons-material";
import { getPlatformFromUrl, Platform } from "../../utils/platform-mapping";
import { generateNonce } from "../../services/auth.service";
import { login } from "../../utils/auth";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    "& fieldset": {
      borderColor: "#5f6368",
    },
    "&:hover fieldset": {
      borderColor: "#5f6368",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputLabel-root": {
    color: "#bdc1c6",
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  "& input": {
    fontSize: "1.1rem",
    padding: "16px 14px",
  },
}));

interface PasswordEntryProps {
  email: string;
  onSubmit: (password: string) => void;
  onBack: () => void;
  onForgotPassword: () => void;
}

const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionForm = motion.form;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      when: "beforeChildren",
    },
  },
};

const formItemVariants = {
  hidden: {
    opacity: 0,
    x: 50, // Start from right
  },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.1, // Stagger delay based on index
      ease: [0.4, 0, 0.2, 1], // Custom easing for smooth motion
    },
  }),
};

export const PasswordEntry = ({
  email,
  onSubmit,
  onBack,
  onForgotPassword,
}: PasswordEntryProps) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const platform = getPlatformFromUrl();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!password.trim()) {
      setError("Please enter your password");
      return;
    }

    try {
      setIsLoading(true);
      // await onSubmit(password);

      await login({ email, password }).then(async (res) => {
        console.log("res", res);
        toast.success("Login successful, redirecting to platform");
        const { code, signature } = await generateNonce(platform.id);
        returnToPlatformFallbackPage(platform, code, signature);
      });
    } catch (err) {
      setError("Incorrect password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const returnToPlatformFallbackPage = (
    platform: Platform,
    nonce: string,
    signature: string
  ) => {
    const mainURL =
      process.env.REACT_APP_NODE_ENV === "production"
        ? platform["prod-url"]
        : process.env.REACT_APP_NODE_ENV === "dev"
        ? platform["dev-url"]
        : platform["local-url"];

    const fallbackURL = `${mainURL}/auth/callback?code=${nonce}&signature=${signature}`;
    window.location.href = fallbackURL;
  };

  return (
    <AuthLayout>
      <MotionBox
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 8, md: 16 },
          alignItems: { xs: "stretch", md: "center" },
          width: "100%",
          height: "100%",
          justifyContent: "center",
          minHeight: { xs: "300px", md: "340px" },
        }}
      >
        {/* Left Column - Header - No animations */}
        <Box
          sx={{
            flex: { md: "0 1 40%" },
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <AuthHeader title="Welcome" />
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{
              color: "#2563EB",
              fontSize: "0.9rem",
              mt: 3,
              pl: 0,
              "&:hover": {
                backgroundColor: "transparent",
                color: "#1D4ED8",
              },
            }}
          >
            {email}
          </Button>
        </Box>

        {/* Right Column - Form */}
        <MotionForm
          onSubmit={handleSubmit}
          variants={containerVariants}
          style={{
            flex: "0 1 60%",
            display: "flex",
            flexDirection: "column",
            gap: "40px",
            paddingBottom: "60px", // Add padding to avoid overlap with footer/badge
          }}
        >
          <AnimatePresence mode="wait">
            <MotionBox
              key="password-field"
              custom={0}
              variants={formItemVariants}
            >
              <StyledTextField
                fullWidth
                label="Enter your password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                error={!!error}
                helperText={error}
                required
                autoComplete="current-password"
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MotionBox>

            <MotionBox
              key="button-group"
              custom={1}
              variants={formItemVariants}
            >
              <Stack
                direction="row"
                spacing={3}
                justifyContent={{ xs: "center", sm: "space-between" }}
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Button
                  onClick={onForgotPassword}
                  size="large"
                  disabled={isLoading}
                  sx={{
                    color: "#2563EB",
                    fontSize: "0.95rem",
                    py: 1.5,
                    px: 3,
                    display: { xs: "none", sm: "inline-flex" }, // Hide on mobile to save space
                    "&:hover": {
                      backgroundColor: "rgba(37, 99, 235, 0.08)",
                    },
                    "&:disabled": {
                      color: "rgba(37, 99, 235, 0.5)",
                    },
                  }}
                >
                  Forgot password?
                </Button>
                <MotionButton
                  key="next-button"
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  loading={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    bgcolor: "#2563EB",
                    color: "#FFFFFF",
                    fontSize: "0.95rem",
                    py: 1.5,
                    px: 5,
                    minWidth: { xs: "100%", sm: "120px" },
                    "&:hover": {
                      bgcolor: "#1D4ED8",
                    },
                    "&:disabled": {
                      bgcolor: "rgba(37, 99, 235, 0.5)",
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
                  ) : (
                    "Next"
                  )}
                </MotionButton>
              </Stack>

              {/* Mobile-only forgot password link */}
              <Box
                sx={{
                  display: { xs: "block", sm: "none" },
                  textAlign: "center",
                  mt: 2,
                }}
              >
                <Button
                  onClick={onForgotPassword}
                  disabled={isLoading}
                  sx={{
                    color: "#2563EB",
                    fontSize: "0.9rem",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "rgba(37, 99, 235, 0.08)",
                    },
                  }}
                >
                  Forgot password?
                </Button>
              </Box>
            </MotionBox>
          </AnimatePresence>
        </MotionForm>
      </MotionBox>
    </AuthLayout>
  );
};
